import React, {SyntheticEvent, useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {Box, Tabs} from '@mui/material';
import apiCall, {setUserToken} from '@/utils/api';
import {Filters} from './Filters';
import {useE4ImpactProjectsContext} from './context';
import StyledTab from '@/shared/Tabs/StyledTab';
import TabPanel from '@/shared/Tabs/TabPanel';
import {getValueByType} from '@/containers/E4ImpactMBA/helpers/mapping';
import Loader from '@/shared/Loader';
import {Impact, LongTermOutcomes, MidTermOutcomes, Output, ShortTermOutcomes} from './TabSections';
import {IFieldProps} from '@/resources/Field';

const tabs = [
    {
        title: 'OUTPUT',
        content: <Output/>
    },
    {
        title: 'SHORT-TERM OUTCOMES',
        content: <ShortTermOutcomes/>
    },
    {
        title: 'MID-TERM OUTCOMES',
        content: <MidTermOutcomes/>
    },
    {
        title: 'LONG-TERM OUTCOMES',
        content: <LongTermOutcomes/>
    },
    {
        title: 'IMPACT',
        content: <Impact/>
    }
];

interface ISearch {
    selectedUoas: number[],
    selectedProjects: string[],
    selectedGender: string,
    selectedSectors: string[],
}

export default function E4ImpactProjects() {
    const [searchParams] = useSearchParams();
    const containerId = searchParams.get('container_id');
    const typeId = searchParams.get('type_id');
    const baselineId = searchParams.get('baseline_id');
    const baselineAccelerationProgramId = searchParams.get('baseline_acceleration_program_id');
    const finalAccelerationProgramId = searchParams.get('final_acceleration_program_id');
    const finalId = searchParams.get('final_id');
    const [tab, setTab] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [genderField, setGenderField] = useState<IFieldProps | undefined>(undefined);
    const [search, setSearch] = useState<ISearch>({
        selectedUoas: [],
        selectedProjects: [],
        selectedGender: 'All',
        selectedSectors: [],
    });
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);
    const [breakdownItems, setBreakdownItems] = useState<any[]>([]);

    const {
        fields,
        setFilterUoas,
        setUoas,
        setFields,
        setBreakdowns,
        setProjectsBreakdown,
        setQuestionsChoices,
        setBaselineSubmissions,
        setFinalSubmissions,
        setBaselineAccelerationProgramSubmissions,
        setFinalAccelerationProgramSubmissions
    } = useE4ImpactProjectsContext()

    const businessSectorQuestionId = getValueByType(typeId || '', 'type_of_sector');

    setUserToken(searchParams.get('token') || '');

    const getDataFilter = () => ({
        ...((search.selectedUoas.length > 0) && {uoa_ids: search.selectedUoas}),
        ...((search.selectedProjects.length > 0) && {level_ids: search.selectedProjects}),
        ...(genderField && search.selectedGender !== 'All' && {custom_fields: {[genderField.id]: search.selectedGender}}),
    });

    const getSubmissionsFilter = (surveyId: string | null) => ({
        survey_id: surveyId,
        completed: 1,
        ...getDataFilter(),
        additional_data: false,
    });

    const filterSubmissionsByBusinessSector = function (submissions: any[]) {
        return submissions.filter((item: any) => search.selectedSectors.length === 0 || search.selectedSectors.includes(item[businessSectorQuestionId]));
    };

    const getData = async () => {
        if (!(containerId && typeId)) return;

        setIsLoaded(false);

        const {data: uoAs} = await apiCall({
            url: `containers/${containerId}/uoa/search`,
            params: {
                container_id: containerId,
                pagination: false,
                type_id: typeId,
                ...getDataFilter(),
            },
        });
        const {data: baselineSubmissions} = await apiCall({
            url: `containers/${containerId}/submissions`,
            params: getSubmissionsFilter(baselineId),
        });
        const {data: baselineAccelerationProgramSubmissions} = await apiCall({
            url: `containers/${containerId}/submissions`,
            params: getSubmissionsFilter(baselineAccelerationProgramId),
        });
        const {data: finalSubmissions} = await apiCall({
            url: `containers/${containerId}/submissions`,
            params: getSubmissionsFilter(finalId),
        });
        const {data: finalAccelerationProgramSubmissions} = await apiCall({
            url: `containers/${containerId}/submissions`,
            params: getSubmissionsFilter(finalAccelerationProgramId),
        });

        setUoas(uoAs.data.filter((item: any) => search.selectedUoas.length === 0 || search.selectedUoas.includes(item.id)));
        setBaselineSubmissions(filterSubmissionsByBusinessSector(baselineSubmissions.submissions));
        setFinalSubmissions(finalSubmissions.submissions);
        setBaselineAccelerationProgramSubmissions(baselineAccelerationProgramSubmissions.submissions);
        setFinalAccelerationProgramSubmissions(finalAccelerationProgramSubmissions.submissions);
    };

    const getFilterData = async () => {
        if (!(containerId && typeId)) return;

        setIsReady(false);

        const {data: uoAs} = await apiCall({
            url: `containers/${containerId}/uoa/search`,
            params: {
                container_id: containerId,
                pagination: false,
                type_id: typeId,
            },
        });
        const {data: fields} = await apiCall({
            url: `containers/${containerId}/fields`,
            params: {
                container_id: containerId,
                pagination: 'false',
            },
        });
        const {data: breakdowns} = await apiCall({
            url: `containers/${containerId}/breakdowns`,
            params: {
                container_id: containerId,
            },
        });
        const {data: questionsChoices} = await apiCall({
            url: `containers/${containerId}/surveys/questions/choices`,
            params: {
                surveyId: baselineId,
                questionId: businessSectorQuestionId,
            },
        });

        setFilterUoas(uoAs.data);
        setFields(fields.custom_fields);
        setBreakdowns(breakdowns.break_downs);
        setBreakdownItems(breakdowns.break_downs);
        getProjectsBreakdown(breakdowns.break_downs);
        setQuestionsChoices(questionsChoices.choices);
    };


    const getProjectsBreakdown = (items: any[]) => {
        const item = items.find((breakdown: any) => breakdown.slug === 'projects');

        if (item) {
            const countries = item.levels.filter((level: any) => !level.parent_id);
            let projects = item.levels.filter((level: any) => level.parent_id);

            if (search.selectedProjects.length > 0) {
                projects = projects.filter((level: any) => search.selectedProjects.includes(level.id));
            }

            const levels = [
                ...countries,
                ...projects
            ];

            setProjectsBreakdown({
                ...item,
                levels
            });
        } else {
            setProjectsBreakdown(undefined);
        }
    }

    const clearSearch = () => {
        setIsLoaded(false);
        setSearch({
            selectedUoas: [],
            selectedProjects: [],
            selectedGender: 'All',
            selectedSectors: [],
        });
        setReloadSearch(!reloadSearch);
    }

    const runSearch = () => {
        setIsLoaded(false);
        setReloadSearch(!reloadSearch);
    }

    useEffect(() => {
        if (isReady) {
            getData().then(_ => setIsLoaded(true));
        }
    }, [isReady, reloadSearch]);

    useEffect(() => {
        getProjectsBreakdown(breakdownItems);
    }, [reloadSearch]);

    useEffect(() => {
        getFilterData().then(() => setIsReady(true));
    }, []);

    useEffect(() => {
        const gender = fields.find((field: IFieldProps) => field.key === 'genere');

        if (gender) {
            setGenderField(gender);
        }
    }, [fields])

    return isLoaded ? (
        <div className='container space-y-8'>
            <Filters
                search={search}
                setSearch={(value) => setSearch(value)}
                clearSearch={() => clearSearch()}
                runSearch={() => runSearch()}
            />
            <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                <Tabs
                    value={tab}
                    onChange={(_: SyntheticEvent, newValue: number) => setTab(newValue)}
                    aria-label='tabs'
                >
                    {tabs.map(({title}, index) =>
                        <StyledTab
                            key={index}
                            label={title}
                        />
                    )}
                </Tabs>
            </Box>
            {tabs.map(({content}, index) =>
                <TabPanel
                    key={index}
                    value={tab}
                    index={index}
                >
                    {content}
                </TabPanel>
            )}
        </div>
    ) : (
        <div className='loader-container'>
            <Loader size={60}/>
        </div>
    )
}